import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartMixed } from '@fortawesome/pro-light-svg-icons/faChartMixed';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { SerializedAccountTracker } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../common/types';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { TrackerRow } from './TrackerRow';
import { TrackersEmptyState } from './TrackersEmptyState';
import { SupportedTrackerChips } from './SupportedTrackerChips';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './SMLTrackers.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TrackersPanelProps {
  trackers: SerializedAccountTracker[];
  onAdd: () => void;
  onRemove: (tracker: SerializedAccountTracker) => void;
}

/** What this artist already fires, and what it is allowed to fire (design artist-settings b1). */
const TrackersPanelComponent: React.FC<TrackersPanelProps> = ({ trackers, onAdd, onRemove }) => {
  const intl = useIntl();

  return (
    <Panel>
      <PanelHead
        icon={<FontAwesomeIcon icon={faChartMixed} />}
        title={intl.formatMessage({ id: 'app.sml.breadcrumbs.trackers', defaultMessage: 'Trackers' })}
        right={
          trackers.length === 0 ? (
            intl.formatMessage({ id: 'app.sml.trackers.panel.none', defaultMessage: 'none' })
          ) : (
            <>
              {intl.formatMessage(
                { id: 'app.sml.trackers.panel.count', defaultMessage: '{count, plural, other {# added}}' },
                { count: trackers.length }
              )}
              <SMLButton icon={<FontAwesomeIcon icon={faPlus} />} onClick={onAdd}>
                {intl.formatMessage({ id: 'app.sml.trackers.add', defaultMessage: 'Add tracker' })}
              </SMLButton>
            </>
          )
        }
      />
      {trackers.length === 0 ? (
        <TrackersEmptyState onAdd={onAdd} />
      ) : (
        <div className={styles.rows}>
          {trackers.map((tracker) => (
            <TrackerRow key={tracker.id} tracker={tracker} onRemove={onRemove} />
          ))}
        </div>
      )}
      <div className={styles.supported}>
        <span className={styles.supportedLabel}>
          {intl.formatMessage({ id: 'app.sml.trackers.supported', defaultMessage: 'Supported:' })}
        </span>
        <SupportedTrackerChips />
      </div>
    </Panel>
  );
};

export const TrackersPanel = TypedMemo(TrackersPanelComponent);
